import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, Search, Send, Maximize2, ArrowLeft, Link as LinkIcon, ChevronRight, Plus, Users, UserPlus, DollarSign } from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { useChatContext } from "@/contexts/chat-context";
import { useToast } from "@/hooks/use-toast";
import { CommunicateSidebar } from "@/components/communicate/CommunicateSidebar";
import { PeopleView } from "@/components/communicate/PeopleView";
import { SharesView } from "@/components/communicate/SharesView";
import { useChatRooms, useChatRoomMessages, useSendChatRoomMessage, useSendInvitation } from "@/lib/queries/community";
import { apiRequest } from "@/lib/queryClient";
import { Label } from "@/components/ui/label";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Thread {
  conversation_id: string;
  other_user_id: string;
  other_user_name: string;
  other_user_email: string;
  last_message_content: string | null;
  last_message_sender_id: string | null;
  last_message_at: string | null;
  unread_count: number;
}

export interface Message {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
  sender_name: string;
  context_type?: string | null;
  context_title?: string | null;
  context_url?: string | null;
}

export interface CommunicateUser {
  id: string;
  display_name: string;
  email: string;
  relationship_type: "assistant" | "family" | "coach" | "manager" | "friend";
  relationship_label: "Assistant" | "Family member" | "Coach" | "Manager" | "Friend";
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function dayLabel(d: string) {
  const date = new Date(d);
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEEE, MMMM d");
}

function groupByDay(messages: Message[]) {
  const groups: { label: string; items: Message[] }[] = [];
  for (const msg of messages) {
    const label = dayLabel(msg.created_at);
    const last = groups[groups.length - 1];
    if (last?.label === label) last.items.push(msg);
    else groups.push({ label, items: [msg] });
  }
  return groups;
}

export function initials(name: string) {
  return name.split(" ").map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";
}

export function avatarColor(name: string) {
  const colors = ["bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-orange-500", "bg-pink-500", "bg-cyan-500", "bg-amber-500"];
  let hash = 0;
  for (const c of name) hash = (hash * 31 + c.charCodeAt(0)) & 0xffff;
  return colors[hash % colors.length];
}

// ── Message bubble ────────────────────────────────────────────────────────────

function MessageBubble({ msg, currentUserId, onContextClick }: {
  msg: Message;
  currentUserId: string;
  onContextClick: (url: string, userId: string, userName: string) => void;
}) {
  const isMine = msg.sender_id === currentUserId;

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} group`}>
      <div className="max-w-[72%] space-y-1">
        {!isMine && <p className="text-[11px] text-muted-foreground ml-1">{msg.sender_name}</p>}
        <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isMine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
          {msg.content}
        </div>
        {msg.context_title && msg.context_url && (
          <button
            className="flex items-center gap-1.5 text-xs text-primary hover:underline ml-1"
            onClick={() => onContextClick(msg.context_url!, msg.sender_id, msg.sender_name)}
          >
            <LinkIcon className="w-3 h-3 shrink-0" />
            <span className="truncate">{msg.context_title}</span>
            <ChevronRight className="w-3 h-3 shrink-0" />
          </button>
        )}
        <p className={`text-[10px] text-muted-foreground ${isMine ? "text-right" : "ml-1"}`}>
          {format(new Date(msg.created_at), "h:mm a")}
        </p>
      </div>
    </div>
  );
}

export function InviteDialog({ type }: { type: "coach" | "assistant" | "family" | "friend" }) {
  const { toast } = useToast();
  const search = useSearch();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [dailyBudget, setDailyBudget] = useState<string>(type === "assistant" ? "5.00" : "");
  const [step, setStep] = useState<"details" | "review">("details");
  useEffect(() => {
    if (new URLSearchParams(search).get("invite") === type) setOpen(true);
  }, [search, type]);

  const sendFriendMutation = useSendInvitation();
  const qc = useQueryClient();
  const inviteAssistantMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/assistants/invitations", data);
      return res.json();
    },
    onSuccess: (result: any) => {
      qc.invalidateQueries({ queryKey: ["/api/assistants/invitations"] });
      setOpen(false);
      if (result?.emailSent === false) {
        toast({ title: "Invitation created, but email failed", variant: "destructive" });
      } else {
        toast({ title: "Invitation sent", description: "The invitation has been sent successfully." });
      }
    },
    onError: (error: any) => toast({ title: "Failed to send invitation", description: error?.message, variant: "destructive" }),
  });

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setHourlyRate("");
      setDailyBudget(type === "assistant" ? "5.00" : "");
      setStep("details");
    }
    setOpen(newOpen);
  };

  const handleSubmit = async () => {
    if (!email.trim()) return;
    const budgetCents = dailyBudget.trim() ? Math.round(Number(dailyBudget) * 100) : undefined;
    if (dailyBudget.trim() && (!Number.isFinite(budgetCents) || budgetCents! < 0)) {
      toast({ title: "Enter a valid daily limit", variant: "destructive" });
      return;
    }
    if (type === "friend" || type === "family") {
      try {
        const result: any = await sendFriendMutation.mutateAsync({
          email: email.trim(),
          ...(type === "family" ? { relationshipType: "family" as const } : {}),
        });
        if (result?.emailSent === false) {
          toast({ title: "Invitation created, but email failed", variant: "destructive" });
        } else {
          toast({ title: "Invitation sent", description: `Invited ${email}` });
        }
        setOpen(false);
      } catch (err: any) {
        toast({ title: "Failed to send invitation", description: err.message, variant: "destructive" });
      }
    } else {
      inviteAssistantMutation.mutate({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        assistantType: type,
        hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
        dailyBudgetCents: budgetCents,
      });
    }
  };

  const isPending = sendFriendMutation.isPending || inviteAssistantMutation.isPending;
  const title = type === "coach" ? "Invite Coach"
    : type === "assistant" ? "Invite Assistant"
    : type === "family" ? "Invite Family Member"
    : "Invite Friend";
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant={type === "friend" ? "secondary" : "outline"} className="shadow-sm" data-testid={`button-invite-${type}`}>
          <UserPlus className="w-4 h-4 mr-2" /> {title}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription data-testid={`text-invite-${type}-step-${step}`}>
            {step === "details" && "Enter recipient details."}
            {step === "review" && "Review the invitation before sending."}
          </DialogDescription>
        </DialogHeader>

          <div className="space-y-4 py-4">
            {step === "details" && <><div>
            <Label htmlFor={`${type}-email`}>Email Address *</Label>
              <Input id={`${type}-email`} data-testid={`input-invite-${type}-email`} type="email" placeholder="email@example.com" value={email} onChange={e => setEmail(e.target.value)} />
          </div>

            {type !== "friend" && type !== "family" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`${type}-firstName`}>First Name</Label>
                  <Input id={`${type}-firstName`} placeholder="John" value={firstName} onChange={e => setFirstName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor={`${type}-lastName`}>Last Name</Label>
                  <Input id={`${type}-lastName`} placeholder="Doe" value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor={`${type}-hourlyRate`}>Hourly Rate (Optional)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id={`${type}-hourlyRate`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="pl-8"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                  />
                </div>
              </div>
              {type === "assistant" && (
                <div>
                  <Label htmlFor={`${type}-dailyBudget`}>Daily spending limit</Label>
                  <Input
                    id={`${type}-dailyBudget`}
                    data-testid="input-invite-daily-budget"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="5.00"
                    value={dailyBudget}
                    onChange={(e) => setDailyBudget(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">AI and other chargeable use stops at this amount each day.</p>
                </div>
              )}
            </>
          )}</>}
            {step === "review" && <div className="rounded-lg border p-4 space-y-2" data-testid={`invite-${type}-review`}>
              <p className="font-medium">Send to: <span className="font-normal">{email || "No email entered"}</span></p>
              {type === "assistant" ? (
                <div className="rounded-md bg-muted/40 p-3" data-testid="assistant-review-full-access">
                  <p className="text-sm font-semibold">All module access enabled</p>
                  <p className="text-xs text-muted-foreground">Standard Assistants can use the enabled modules. Manager-owned records still require individual sharing.</p>
                </div>
              ) : (
                <div className="rounded-md bg-muted/40 p-3" data-testid={`${type}-review-individual-shares`}>
                  <p className="text-sm font-semibold">Individual shares only</p>
                  <p className="text-xs text-muted-foreground">This relationship does not provide blanket access to Notes/Notepad, Goals, Calendar, Tasks, Habits, or Tracker-X. Access is only to records that are shared individually.</p>
                </div>
              )}
            </div>}
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid={`button-invite-${type}-${step === "details" ? "cancel" : "back"}`} onClick={() => step === "details" ? handleOpenChange(false) : setStep("details")}>{step === "details" ? "Cancel" : "Back"}</Button>
          {step === "details" && <Button data-testid={`button-invite-${type}-next-review`} onClick={() => setStep("review")} disabled={!email.trim()}>Review invitation</Button>}
          {step === "review" && <Button data-testid={`button-invite-${type}-send`} onClick={handleSubmit} disabled={!email.trim() || isPending}>{isPending ? "Sending..." : "Send Invitation"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CommunicatePage() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const threadFromUrl = params.get("thread");
  const viewFromUrl = params.get("view");

  const { openFloat, setReturnThread } = useChatContext();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [activeView, setActiveView] = useState<"inbox" | "people" | "shares">(
    viewFromUrl === "shares" ? "shares" : viewFromUrl === "people" ? "people" : "inbox",
  );
  const [activeUserId, setActiveUserId] = useState<string | null>(threadFromUrl);
  const [composeOpen, setComposeOpen] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");

  useEffect(() => {
    if (viewFromUrl === "shares" || viewFromUrl === "people") {
      setActiveView(viewFromUrl);
      setActiveUserId(null);
    }
  }, [viewFromUrl]);

  const isGroupChat = activeUserId?.startsWith("room_");
  const actualChatId = isGroupChat ? activeUserId!.replace("room_", "") : activeUserId;

  const { data: threads = [], refetch: refetchThreads } = useQuery<Thread[]>({
    queryKey: ["/api/communicate/threads"],
    queryFn: async () => { const res = await fetch("/api/communicate/threads"); return res.ok ? res.json() : []; },
  });

  const { data: allUsers = [] } = useQuery<CommunicateUser[]>({
    queryKey: ["/api/communicate/users"],
    queryFn: async () => { const res = await fetch("/api/communicate/users"); return res.ok ? res.json() : []; },
  });

  const normalizedRecipientSearch = recipientSearch.trim().toLowerCase();
  const visibleRecipients = normalizedRecipientSearch
    ? allUsers.filter(u => u.display_name.toLowerCase().includes(normalizedRecipientSearch) || u.email?.toLowerCase().includes(normalizedRecipientSearch))
    : allUsers;

  const selectThread = (userId: string) => {
    setActiveView("inbox");
    setActiveUserId(userId);
    setComposeOpen(false);
    navigate(`/communicate?thread=${userId}`, { replace: true });
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-background">
      {/* ── LEFT PANEL: Main Navigation Sidebar ──────────────────────────────────── */}
      <CommunicateSidebar
        activeView={activeView}
        setActiveView={setActiveView}
        activeUserId={activeUserId}
        setActiveUserId={setActiveUserId}
      />

      {/* ── RIGHT PANEL: Main Content Area ────────────────────────────────────────── */}
      <div
        className={`${activeView === "inbox" && !activeUserId ? "hidden md:flex" : "flex"} flex-1 flex-col min-w-0 bg-background`}
      >
        {activeView !== "inbox" && (
          <div className="md:hidden px-3 py-2 border-b">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActiveView("inbox");
                setActiveUserId(null);
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Communicate
            </Button>
          </div>
        )}
        {activeView === "people" && <PeopleView />}
        {activeView === "shares" && <SharesView />}

        {activeView === "inbox" && (
          <div className="px-6 py-4 border-b bg-card shrink-0 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Inbox</h2>
              <p className="text-sm text-muted-foreground">Collaborate with your network and team.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <InviteDialog type="coach" />
              <InviteDialog type="assistant" />
              <InviteDialog type="family" />
              <InviteDialog type="friend" />
            </div>
          </div>
        )}

        {activeView === "inbox" && !activeUserId && (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <MessageSquare className="w-12 h-12 opacity-20" />
            <p className="font-medium">Select a conversation to start messaging</p>
            <p className="text-sm">Message your team and accepted friends.</p>
            <Button
              variant="outline"
              onClick={() => setComposeOpen(true)}
              data-testid="button-new-message"
            >
              <Plus className="w-4 h-4 mr-2" /> New Message
            </Button>
          </div>
        )}

        {activeView === "inbox" && activeUserId && !isGroupChat && (
          <DirectChatView
            activeUserId={activeUserId}
            threads={threads}
            allUsers={allUsers}
            qc={qc}
            refetchThreads={refetchThreads}
            setReturnThread={setReturnThread}
            openFloat={openFloat}
            navigate={navigate}
            onBack={() => {
              setActiveUserId(null);
              navigate("/communicate", { replace: true });
            }}
          />
        )}

        {activeView === "inbox" && activeUserId && isGroupChat && (
          <GroupChatView
            roomId={actualChatId!}
          />
        )}
      </div>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md max-h-[85dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle>New Message</DialogTitle>
            <DialogDescription>Choose an assistant, coach, manager, or accepted friend.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              className="pl-9"
              placeholder="Search by name, email, or relationship…"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
              data-testid="input-recipient-search"
            />
          </div>
          <ScrollArea className="min-h-0 flex-1 -mx-2 px-2">
            <div className="space-y-1 py-1">
              {visibleRecipients.map((user) => (
                <button
                  key={user.id}
                  className="w-full flex items-center gap-3 rounded-lg p-3 text-left hover:bg-muted"
                  onClick={() => selectThread(user.id)}
                  data-testid={`recipient-${user.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${avatarColor(user.display_name)}`}>
                    {initials(user.display_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{user.display_name}</p>
                      <Badge variant="secondary" className="text-[10px] shrink-0">{user.relationship_label}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Direct Chat View Component ────────────────────────────────────────────────
function DirectChatView({ activeUserId, threads, allUsers, qc, refetchThreads, setReturnThread, openFloat, navigate, onBack }: any) {
  const { toast } = useToast();
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState("");

  const activeUserName = threads.find((t: any) => t.other_user_id === activeUserId)?.other_user_name
    || allUsers.find((u: any) => u.id === activeUserId)?.display_name
    || "Unknown User";

  const activeUser = allUsers.find((user: any) => user.id === activeUserId);

  const { data: threadData, refetch: refetchMessages } = useQuery<{ conversationId: string | null; messages: Message[]; }>({
    queryKey: ["/api/communicate/thread", activeUserId],
    queryFn: async () => {
      const res = await fetch(`/api/communicate/thread/${activeUserId}`);
      return res.ok ? res.json() : { conversationId: null, messages: [] };
    },
    enabled: !!activeUserId,
    refetchInterval: 5000,
  });

  const messages = threadData?.messages ?? [];
  const currentUserId = messages.find((m) => m.sender_id !== activeUserId)?.sender_id
    ?? threads.find((t: any) => t.other_user_id === activeUserId)?.last_message_sender_id
    ?? "";

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(`/api/communicate/thread/${activeUserId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error("Failed to send");
      return res.json();
    },
    onSuccess: () => {
      setInput("");
      refetchMessages();
      refetchThreads();
      qc.invalidateQueries({ queryKey: ["/api/communicate/unread-count"] });
      inputRef.current?.focus();
    },
    onError: () => toast({ title: "Failed to send message", variant: "destructive" }),
  });

  const handleSend = () => {
    if (!input.trim() || sendMutation.isPending) return;
    sendMutation.mutate(input.trim());
  };

  const handleContextClick = (url: string, senderUserId: string, senderName: string) => {
    setReturnThread({
      userId: senderUserId === currentUserId ? activeUserId! : senderUserId,
      userName: senderUserId === currentUserId ? activeUserName : senderName,
    });
    navigate(url);
  };

  const groups = groupByDay(messages);

  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="flex items-center gap-3 px-5 py-3.5 border-b bg-background shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onBack}
          data-testid="button-communicate-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="sr-only">Back to conversations</span>
        </Button>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${avatarColor(activeUserName)}`}>
          {initials(activeUserName)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{activeUserName}</p>
          {activeUser?.relationship_label && <p className="text-xs text-muted-foreground">{activeUser.relationship_label}</p>}
        </div>
        <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => openFloat(activeUserId, activeUserName)}>
          <Maximize2 className="w-3.5 h-3.5" /> Pop out
        </Button>
      </div>

      <ScrollArea className="flex-1 px-6 py-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground gap-2">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold ${avatarColor(activeUserName)}`}>
              {initials(activeUserName)}
            </div>
            <p className="font-medium mt-2">{activeUserName}</p>
            <p className="text-sm">Send a message to start the conversation.</p>
          </div>
        ) : (
          <div className="space-y-5 max-w-3xl mx-auto">
            {groups.map((group) => (
              <div key={group.label}>
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{group.label}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-3">
                  {group.items.map((msg) => (
                    <MessageBubble key={msg.id} msg={msg} currentUserId={currentUserId} onContextClick={handleContextClick} />
                  ))}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>

      <div className="px-6 py-4 border-t bg-background shrink-0">
        <div className="max-w-3xl mx-auto flex gap-3 items-end">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={`Message ${activeUserName}…`}
            className="min-h-[52px] max-h-40 resize-none text-sm"
            rows={2}
            disabled={sendMutation.isPending}
            data-testid="input-communicate-message"
          />
          <Button size="sm" className="h-10 w-10 p-0 shrink-0" disabled={!input.trim() || sendMutation.isPending} onClick={handleSend}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Group Chat View Component ─────────────────────────────────────────────────
function GroupChatView({ roomId }: { roomId: string }) {
  const { data: chatRooms = [] } = useChatRooms();
  const room = chatRooms.find(r => r.id === roomId);
  const { data: messages = [] } = useChatRoomMessages(roomId);
  const sendMutation = useSendChatRoomMessage();
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  if (!room) return null;

  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="flex items-center gap-3 px-5 py-3.5 border-b bg-background shrink-0">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center bg-primary text-white text-sm font-bold shrink-0`}>
          <Users className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{room.name}</p>
          <p className="text-xs text-muted-foreground">{room.members.length} members</p>
        </div>
      </div>

      <ScrollArea className="flex-1 px-6 py-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground gap-2">
            <Users className="w-10 h-10 opacity-20" />
            <p className="font-medium mt-2">{room.name}</p>
            <p className="text-sm">Send a message to start the group chat.</p>
          </div>
        ) : (
          <div className="space-y-5 max-w-3xl mx-auto">
            {messages.map((msg: any) => (
              <div key={msg.id} className="flex justify-start group">
                <div className="max-w-[72%] space-y-1">
                  <p className="text-[11px] text-muted-foreground ml-1">{msg.sender?.firstName} {msg.sender?.lastName}</p>
                  <div className="rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-muted text-foreground">
                    {msg.content}
                  </div>
                  <p className="text-[10px] text-muted-foreground ml-1">
                    {format(new Date(msg.createdAt), "h:mm a")}
                  </p>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>

      <div className="px-6 py-4 border-t bg-background shrink-0">
        <div className="max-w-3xl mx-auto flex gap-3 items-end">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (input.trim() && !sendMutation.isPending) {
                  sendMutation.mutate({ roomId, content: input.trim() }, { onSuccess: () => setInput("") });
                }
              }
            }}
            placeholder={`Message ${room.name}…`}
            className="min-h-[52px] max-h-40 resize-none text-sm"
            rows={2}
            disabled={sendMutation.isPending}
          />
          <Button size="sm" className="h-10 w-10 p-0 shrink-0" disabled={!input.trim() || sendMutation.isPending} onClick={() => {
            sendMutation.mutate({ roomId, content: input.trim() }, { onSuccess: () => setInput("") });
          }}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
